import { useEffect, useMemo, useRef, useState } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import useAppMethods from '../../hooks/utility/useAppMethods';
import { DeliveryMode, DropoffAction, ORDER_METHODS, REDUX_STORE_SLICES } from '../../services/exports/Constants';
import useOrderFlow from '../../hooks/order/useOrderFlow';
import { SectionType } from './sections/CheckoutInfoBag';
import collect from 'collect.js';
import useMobileApp from '../../hooks/mobile-app/useMobileApp';
import useModalRouter from '../../modules/mobile/common/hooks/useModalRouter';
import { Modals } from '../../modules/mobile/exports/Enums';
import useStore from '../../hooks/store/useStore';
import HelperMethods from '../../services/exports/HelperMethods';
import moment from 'moment';
import AddressHelpers from '../../services/helpers/AddressHelpers';
import { OrderMethodModalProps } from '../../services/exports/Interfaces';
import TextInput from '../inputs/TextInput';
import TextArea from '../inputs/TextArea';
import OrderMethodIcon from '../../assets/icons/order-methods/OrderMethodIcon';
import useTheme from '../../hooks/ui/useTheme';
import useScreenType from '../../hooks/utility/useScreenType';

interface Props {
  toggleSection: (section: SectionType, open: boolean) => any;
}

export default function OrderInfo(props: Props) {
  const { toggleSection } = props;
  const { toggleOrderMethodModal } = useAppMethods();
  const { localData, update } = useOrderFlow();

  const { isMobileApp } = useMobileApp();
  const { openModal } = useModalRouter();

  const invoiceInfo = collect([
    localData.invoice_company_name,
    localData.invoice_tax_id,
    localData.invoice_address,
    localData.invoice_email,
  ])
    .filter((item) => Boolean(item))
    .implode(', ');

  useEffect(() => {
    !localData.delivery_dropoff_action && update('delivery_dropoff_action', DropoffAction.RingDoorBell);
  }, []);

  useEffect(() => {
    localData.is_receipt_required && !invoiceInfo && toggleSection(SectionType.invoice_info, true);
  }, [localData.is_receipt_required]);

  const onChangeOrderMethod = (modalProps?: Partial<OrderMethodModalProps>): void => {
    if (isMobileApp) {
      return openModal({
        id: Modals.OrderMethod,
        props: modalProps,
      });
    }

    toggleOrderMethodModal({ openModal: true, ...modalProps });
  };

  return (
    <div className="space-y-md">
      <OrderInfo.Fulfillment onChange={onChangeOrderMethod} />
    </div>
  );
}

OrderInfo.Fulfillment = ({ onChange }: { onChange: (props?: Partial<OrderMethodModalProps>) => void }) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'Components:Checkout:OrderInfo' });

  const theme = useTheme();
  const { company } = useStore<REDUX_STORE_SLICES.INITIAL_DATA>(REDUX_STORE_SLICES.INITIAL_DATA);
  const { cached_order } = useStore<REDUX_STORE_SLICES.ORDER>(REDUX_STORE_SLICES.ORDER);
  const { localData, update } = useOrderFlow();
  const { profile } = useStore<REDUX_STORE_SLICES.PROFILE>(REDUX_STORE_SLICES.PROFILE);
  const [detailsExpanded, setDetailsExpanded] = useState(false);
  const [noteExpanded, setNoteExpanded] = useState(false);
  const savedNoteOffered = useRef(false);

  const { isMobile } = useScreenType();

  const method = cached_order.method ?? ORDER_METHODS.PICKUP;

  const methodLabel = t(`sections.method.${method}.title`, {
    defaultValue: t(`sections.method.title`),
  });

  const timeLabel = useMemo(() => {
    if (cached_order.scheduled_for) {
      return moment(cached_order.scheduled_for).format('ddd HH:mm, MMM DD');
    }

    const averageTime =
      method === ORDER_METHODS.DELIVERY
        ? company?.average_order_delivery_time
        : company?.average_order_preparation_time;

    return t(`sections.time.method.asap.title`, {
      time: HelperMethods.formatTimeInterval(averageTime),
    });
  }, [
    cached_order.scheduled_for,
    method,
    company?.average_order_delivery_time,
    company?.average_order_preparation_time,
  ]);

  const addressLabel = useMemo(() => {
    const format = company?.region?.address_format;

    if (method === ORDER_METHODS.DELIVERY) {
      return AddressHelpers.format({
        street_name: cached_order.delivery_street_name,
        street_number: cached_order.delivery_street_number,
        city: cached_order.delivery_city,
        zip_code: cached_order.delivery_zip_code,
        format,
      });
    }

    if (method === ORDER_METHODS.ROOM_SERVICE) {
      return collect([
        cached_order.room_service_location?.name,
        AddressHelpers.format({
          street_name: cached_order.room_service_location?.street_name,
          street_number: cached_order.room_service_location?.street_number,
          format,
        }),
        cached_order.room_service_details
          ? `${t(`${cached_order.room_service_location?.type}.details_label`)}: ${cached_order.room_service_details}`
          : null,
      ])
        .whereNotNull()
        .implode(' · ');
    }

    return `${company?.name ?? ''}, ${AddressHelpers.format({
      street_name: company?.address,
      city: company?.city,
      zip_code: company?.zip_code,
      format,
    })}`;
  }, [
    method,
    company,
    cached_order.delivery_street_name,
    cached_order.delivery_street_number,
    cached_order.delivery_city,
    cached_order.delivery_zip_code,
    cached_order.room_service_location,
    cached_order.room_service_details,
  ]);

  const isDelivery = method === ORDER_METHODS.DELIVERY;
  const isExternalDelivery = isDelivery && company?.delivery_mode === DeliveryMode.External;

  // The profile's note is the one saved from the customer's last delivery order, so it is offered
  // on delivery only, and once: a customer who cleared it must not get it back by switching method.
  // Sent untouched with a pickup order it would reach the restaurant unseen. Runs on method change
  // only, not per keystroke, hence the short dependency list.
  useEffect(() => {
    const savedNote = profile?.customer_note;

    if (!savedNote) {
      return;
    }

    if (isDelivery && !localData.customer_note && !savedNoteOffered.current) {
      savedNoteOffered.current = true;
      update('customer_note', savedNote);
    }

    if (!isDelivery && localData.customer_note === savedNote) {
      update('customer_note', '');
    }
  }, [isDelivery, profile?.customer_note]);

  const hasFilledDetails = Boolean(localData.delivery_doorbell_name || localData.delivery_company_name);

  const isExpanded = detailsExpanded;

  const deliverySummary = useMemo(() => {
    const dropoffLabel = isExternalDelivery
      ? t(`form.dropoff_action.options.${localData.delivery_dropoff_action ?? DropoffAction.RingDoorBell}`)
      : null;

    const labelled = (key: string, value?: string | null): string | null =>
      value ? `${t(`summary.${key}`)}: ${value}` : null;

    return collect([
      dropoffLabel,
      labelled('doorbell', localData.delivery_doorbell_name),
      labelled('company', localData.delivery_company_name),
    ])
      .filter((item) => Boolean(item))
      .implode(' · ');
  }, [
    isExternalDelivery,
    localData.delivery_dropoff_action,
    localData.delivery_doorbell_name,
    localData.delivery_company_name,
    t,
  ]);

  return (
    <div className="rounded-xl border-xs border-mobile-secondary bg-brand-card">
      <div className="flex gap-sm px-md py-sm">
        <span className="h-8 w-8 shrink-0 rounded-full bg-mobile-notice flex items-center justify-center">
          <OrderMethodIcon orderMethod={method} color={theme.color.brand.icon.color} className="h-4 w-4" />
        </span>
        <div className="flex flex-wrap items-center gap-x-xs gap-y-2xs text-sm min-w-0 flex-1">
          {isMobile ? (
            <>
              <span className="font-semibold text-brand-text-primary w-full">{methodLabel}</span>
              <span className="text-muted-foreground w-full">{timeLabel}</span>
              {addressLabel && <span className="mp-mask text-muted-foreground w-full">{addressLabel}</span>}
            </>
          ) : (
            <>
              <span className="font-semibold text-brand-text-primary">{methodLabel}</span>
              <span className="text-muted-foreground">·</span>
              <span className="text-muted-foreground">{timeLabel}</span>
              {addressLabel && (
                <>
                  <span className="text-muted-foreground">·</span>
                  <span className="mp-mask text-muted-foreground truncate">{addressLabel}</span>
                </>
              )}
            </>
          )}
        </div>
        <button
          type="button"
          onClick={() => onChange({ openModal: true, orderMethod: method })}
          className="text-sm font-semibold text-brand-primary hover:underline shrink-0 h-fit"
        >
          {t('buttons.change')}
        </button>
      </div>

      {isDelivery && (
        <div className="px-md pb-md">
          <div className="pl-[44px]">
            {isExpanded ? (
              <div className="space-y-sm">
                {isExternalDelivery && (
                  <div className="flex items-center gap-sm flex-wrap">
                    <span className="text-xs text-muted-foreground shrink-0">
                      {t('sections.delivery_info.on_arrival')}
                    </span>
                    <div className="flex gap-xs flex-wrap">
                      {Object.values(DropoffAction).map((action) => {
                        const active = localData.delivery_dropoff_action === action;

                        return (
                          <button
                            key={action}
                            type="button"
                            onClick={() => update('delivery_dropoff_action', action)}
                            className={classnames(pillStyle, {
                              [pillActiveStyle]: active,
                              [pillInactiveStyle]: !active,
                            })}
                          >
                            {t(`form.dropoff_action.options.${action}`)}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                )}
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-xs">
                  <TextInput
                    type="text"
                    value={localData.delivery_doorbell_name}
                    placeholder={t('form.doorbell_name.placeholder')}
                    onChange={(value) => update('delivery_doorbell_name', value)}
                  />
                  <TextInput
                    type="text"
                    value={localData.delivery_company_name}
                    placeholder={t('form.company_name.placeholder')}
                    onChange={(value) => update('delivery_company_name', value)}
                  />
                </div>
              </div>
            ) : hasFilledDetails ? (
              <p className="text-sm break-words">
                <span className="mp-mask text-muted-foreground">{deliverySummary}</span>{' '}
                <span className="text-muted-foreground">·</span>{' '}
                <button
                  type="button"
                  onClick={() => setDetailsExpanded(true)}
                  className="font-semibold text-brand-primary hover:underline"
                >
                  {t('buttons.edit')}
                </button>
              </p>
            ) : (
              <button type="button" onClick={() => setDetailsExpanded(true)} className={addDetailsButtonStyle}>
                {t('buttons.add_delivery_note')}
              </button>
            )}
          </div>
        </div>
      )}

      <div className="px-md pb-md">
        <div className="pl-[44px]">
          {noteExpanded ? (
            <TextArea
              maxLength={255}
              value={localData.customer_note}
              placeholder={t('form.customer_note.placeholder')}
              onChange={(value) => update('customer_note', value)}
            />
          ) : localData.customer_note ? (
            <p className="text-sm break-words">
              <span className="mp-mask text-muted-foreground">
                {t('summary.note')}: {localData.customer_note}
              </span>{' '}
              <span className="text-muted-foreground">·</span>{' '}
              <button
                type="button"
                onClick={() => setNoteExpanded(true)}
                className="font-semibold text-brand-primary hover:underline"
              >
                {t('buttons.edit')}
              </button>
            </p>
          ) : (
            <button type="button" onClick={() => setNoteExpanded(true)} className={addDetailsButtonStyle}>
              {t('buttons.add_order_note')}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const pillStyle = 'text-xs px-sm py-2xs rounded-full border-xs transition-colors';

const pillActiveStyle = 'border-brand-primary bg-brand-primary/10 text-brand-primary font-medium';

const pillInactiveStyle = 'border-mobile-secondary text-muted-foreground hover:text-brand-text-primary';

const addDetailsButtonStyle =
  'text-sm text-muted-foreground underline decoration-brand-text-secondary/40 hover:text-brand-text-primary';
